import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator, {
  type StatusIndicatorProps,
} from '@cloudscape-design/components/status-indicator';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { formatDate } from '@/lib/format';
import { isCancellationScheduled } from './scheduledCancellation';
import {
  discountedAmountCents,
  localeForCountry,
  needsPayment,
  PaymentProvider,
  PaymentStatus,
  SubscriptionStatus,
  type BillingCycle,
  type AppStoreSubscriptionInfo,
  type GooglePlaySubscriptionInfo,
  type Payment,
  type SubscriptionStatus as SubscriptionStatusT,
} from '@etiquetachef/types';
import {
  subscribeTenantPayments,
  type BillingDocResult,
  type PaymentListResult,
} from '@/lib/firestoreTenantDetail';
import { serverNow } from '@etiquetachef/ui-shared/online';

/**
 * Rótulo de cada ciclo. `Record` sobre o enum: um ciclo novo reprova o
 * typecheck aqui em vez de aparecer como "Mensal" (#679 D13 — trimestral e
 * semestral da venda assistida).
 */
const CYCLE_LABEL_KEY: Record<BillingCycle, string> = {
  monthly: 'tenantDetail.billing.cycleMonthly',
  quarterly: 'tenantDetail.billing.cycleQuarterly',
  semiannual: 'tenantDetail.billing.cycleSemiannual',
  yearly: 'tenantDetail.billing.cycleYearly',
};

type Props = { result: BillingDocResult; tenantId: string };

// Mesma semântica de cor/ícone do `StatusBadge` do tenant (Cloudscape
// StatusIndicator) — mantém o card de Cobrança visualmente alinhado ao
// `TenantInfoCard` (pedido do owner 2026-06-09).
const STATUS_TYPE: Record<SubscriptionStatusT, StatusIndicatorProps.Type> = {
  [SubscriptionStatus.ACTIVE]: 'success',
  [SubscriptionStatus.PAST_DUE]: 'warning',
  [SubscriptionStatus.SUSPENDED]: 'error',
  [SubscriptionStatus.CANCELLED]: 'stopped',
};

function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{value}</div>
    </div>
  );
}

/**
 * Primeiro pagamento (mais antigo) do tenant — o que foi cobrado na ativação.
 *
 * Origem: sem issue — commit 2ccb84ebf, 2026-06-09 17:13 +0200 (cupom + 1º
 * pagamento no detalhe do tenant).
 *
 * Arquitetura: só enxerga os 100 pagamentos mais recentes
 * (`subscribeTenantPayments`); num tenant com mais de 100, o "1º" mostrado é o
 * mais antigo DESSA janela, não o da ativação.
 *
 * Usado por: `BillingCard` (campo "1º pagamento").
 */
function firstPaymentOf(payments: PaymentListResult): Payment | null {
  if (payments.kind !== 'READY' || payments.payments.length === 0) return null;
  // subscribeTenantPayments ordena por created_at DESC — o 1º pagamento é o
  // de menor created_at (último da lista).
  return payments.payments.reduce((earliest, p) =>
    p.created_at < earliest.created_at ? p : earliest,
  );
}

/** Pagamentos que de fato entraram caixa (cobrança realizada). */
const SETTLED_STATUSES: readonly Payment['status'][] = [
  PaymentStatus.CONFIRMED,
  PaymentStatus.PARTIALLY_REFUNDED,
];

type BillingSummary = {
  /** Data da cobrança liquidada mais recente (millis), ou null se nenhuma. */
  lastPaidAt: number | null;
  /** Número de cobranças liquidadas. */
  count: number;
  /** Total líquido pago (cents): soma de `amount` menos `refunded_amount`. */
  totalPaidCents: number;
};

/**
 * Resumo das cobranças do tenant derivado dos pagamentos liquidados
 * (`subscribeTenantPayments`, limite 100). Retorna null enquanto carrega/erro.
 *
 * Origem: sem issue — commit 195dd7dd7, 2026-06-10 11:56 +0200.
 *
 * Contrato: conta só `CONFIRMED`/`PARTIALLY_REFUNDED` com líquido
 * (`amount − refunded_amount`, CENTAVOS) > 0; `lastPaidAt` = `paid_at` ou, sem
 * ele, `created_at` (epoch ms). Mesma janela de 100 do `firstPaymentOf`: total
 * e contagem são da janela, não da vida do tenant.
 *
 * Usado por: `BillingCard` (último pagamento, nº de cobranças, total pago).
 *
 * Falhas conhecidas: 2026-06-28 — pagamentos R$0 e 100 % estornados inflavam
 * a contagem (comentário no corpo).
 */
function summarizePayments(payments: PaymentListResult): BillingSummary | null {
  if (payments.kind !== 'READY') return null;
  // Só conta cobranças que de fato entraram caixa: status liquidado E valor
  // líquido (amount − refunded) > 0. Exclui pagamentos R$0 (artefatos de
  // sandbox/fatura zerada) e os 100% estornados, que inflavam a contagem sem
  // mexer no total (ex.: tenant E2E mostrava "7 cobranças / R$250").
  const settled = payments.payments.filter(
    (p) => SETTLED_STATUSES.includes(p.status) && p.amount - p.refunded_amount > 0,
  );
  let lastPaidAt: number | null = null;
  let totalPaidCents = 0;
  for (const p of settled) {
    totalPaidCents += p.amount - p.refunded_amount;
    const at = p.paid_at ?? p.created_at;
    if (lastPaidAt === null || at > lastPaidAt) lastPaidAt = at;
  }
  return { lastPaidAt, count: settled.length, totalPaidCents };
}

/**
 * Preço do storefront da App Store em texto (`price_milli` é MILÉSIMOS da
 * moeda, formato da Apple — `9990` = 9,99; JPY `1500000` = ¥1.500). `null`
 * sem preço ou moeda. Não passa pelo `MoneyDisplay`, que espera centavos e
 * erraria moeda sem casas decimais (JPY).
 *
 * Origem: #942 — 2026-10-12 12:30 +0200. Usado por: `AppStoreDetails` (este arquivo).
 */
function formatAppStorePrice(info: AppStoreSubscriptionInfo, locale: string): string | null {
  const milli = info.price_milli ?? null;
  const currency = info.currency ?? null;
  if (milli === null || currency === null) return null;
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(milli / 1000);
  } catch {
    // Moeda que o Intl do navegador não conhece: número cru + código.
    return `${String(milli / 1000)} ${currency}`;
  }
}

/**
 * Espelho da assinatura da App Store (`subscription.appstore`), só leitura.
 *
 * Origem: #942 — 2026-10-12 12:30 +0200 (assinatura self-service por IAP no iOS).
 *
 * Por quê: o tenant do IAP não tem fatura, pagamento nem assinatura no nosso
 * gateway — o suporte precisa do `original_transaction_id` (a chave na App Store
 * Connect e no `external_subscription_id = aps_<id>`), do ambiente
 * (Sandbox × Production), do storefront e do preço que a Apple cobra.
 *
 * Contrato: `info` `null`/ausente mostra só o aviso. Nenhuma ação.
 *
 * Usado por: `BillingCard` (este arquivo), quando `external_provider === 'appstore'`.
 *
 * Dados — `tenants/{tid}/billing/subscription.appstore`, escrito pelo servidor
 * (App Store Server Notifications V2 + reconciliação diária):
 *   { "original_transaction_id": "2000000000000001", "product_id": "<produto>",
 *     "environment": "Production", "storefront": "JPN", "price_milli": 1500000,
 *     "currency": "JPY", "offer_type": "FREE_TRIAL", "expires_at": 1760000000000,
 *     "auto_renew": true, "updated_at": 1760000000000 }
 * Schema: `AppStoreSubscriptionInfoSchema` em `packages/types/src/schemas.ts`.
 * `storefront` é alpha-3 (formato da Apple), não o `country_code` do tenant.
 */
function AppStoreDetails({
  info,
  trialEndsAt,
}: {
  info: AppStoreSubscriptionInfo | null;
  trialEndsAt: number | null;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const price = info ? formatAppStorePrice(info, i18n.language) : null;
  return (
    <Box margin={{ top: 'm' }}>
      <Alert type="info" header={t('tenantDetail.billing.appStore.title')}>
        {t('tenantDetail.billing.appStore.notice')}
      </Alert>
      {info ? (
        <Box margin={{ top: 's' }}>
          <ColumnLayout columns={2} variant="text-grid">
            <Field
              label={t('tenantDetail.billing.appStore.originalTransactionId')}
              value={<span style={{ fontFamily: 'monospace' }}>{info.original_transaction_id}</span>}
            />
            <Field label={t('tenantDetail.billing.appStore.environment')} value={info.environment} />
            <Field label={t('tenantDetail.billing.appStore.storefront')} value={info.storefront ?? '—'} />
            <Field label={t('tenantDetail.billing.appStore.price')} value={price ?? '—'} />
            <Field
              label={t('tenantDetail.billing.appStore.product')}
              value={<span style={{ fontFamily: 'monospace' }}>{info.product_id}</span>}
            />
            <Field label={t('tenantDetail.billing.appStore.offerType')} value={info.offer_type ?? '—'} />
            {info.expires_at != null ? (
              <Field
                label={t('tenantDetail.billing.appStore.expiresAt')}
                value={formatDate(info.expires_at, i18n.language)}
              />
            ) : null}
            {trialEndsAt !== null ? (
              <Field
                label={t('tenantDetail.billing.appStore.trialEndsAt')}
                value={formatDate(trialEndsAt, i18n.language)}
              />
            ) : null}
            <Field
              label={t('tenantDetail.billing.appStore.autoRenew')}
              value={
                info.auto_renew == null
                  ? '—'
                  : info.auto_renew
                    ? t('tenantDetail.billing.appStore.autoRenewOn')
                    : t('tenantDetail.billing.appStore.autoRenewOff')
              }
            />
          </ColumnLayout>
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * Preço do Google Play em texto (`price_micros` é MILIONÉSIMOS da moeda, formato
 * da Play — `99000000` = 99,00). `null` sem preço ou moeda. Mesmo motivo do
 * `formatAppStorePrice` para não passar pelo `MoneyDisplay` (centavos).
 *
 * Origem: #969 — 2026-10-14 15:17 +0200 (auditoria M4). Usado por: `GooglePlayDetails` (este arquivo).
 */
function formatGooglePlayPrice(info: GooglePlaySubscriptionInfo, locale: string): string | null {
  const micros = info.price_micros ?? null;
  const currency = info.currency ?? null;
  if (micros === null || currency === null) return null;
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(micros / 1_000_000);
  } catch {
    return `${String(micros / 1_000_000)} ${currency}`;
  }
}

/**
 * Espelho da assinatura do Google Play (`subscription.googleplay`), só leitura.
 *
 * Origem: #969 — 2026-10-14 15:17 +0200 (auditoria M4: o card só mostrava o
 * espelho `appstore`; o tenant do self-service Android aparecia sem nenhum dado
 * da Play).
 *
 * Por quê: o tenant pago pela Play não tem fatura nem assinatura no nosso
 * gateway. O suporte precisa do `purchase_token` (chave da Play Developer API,
 * `purchases.subscriptionsv2.get` — o `external_subscription_id` `gps_…` é só o
 * hash dele), do último pedido (`GPA.…`, o que o cliente vê no recibo), do
 * estado, do país da conta Play e do preço regional que o Google cobra.
 *
 * Contrato: `info` `null`/ausente mostra só o aviso. Nenhuma ação.
 *
 * Usado por: `BillingCard` (este arquivo), quando `external_provider === 'googleplay'`.
 *
 * Dados — `tenants/{tid}/billing/subscription.googleplay`, escrito pelo servidor
 * (Functions `play/*`: ativação, RTDN, reconciliação):
 *   { "purchase_token": "<token opaco>", "product_id": "allinone_monthly",
 *     "base_plan_id": "monthly", "latest_order_id": "GPA.0000-0000-0000-00000",
 *     "region_code": "IT", "state": "SUBSCRIPTION_STATE_ACTIVE",
 *     "price_micros": 99000000, "currency": "EUR", "offer_phase": "FREE_TRIAL",
 *     "expires_at": 1791000000000, "auto_renew": true, "test_purchase": false,
 *     "updated_at": 1790400000000 }
 * Schema: `GooglePlaySubscriptionInfoSchema` em `packages/types/src/schemas.ts`.
 * `region_code` é alpha-2 do país da conta Play, não o `country_code` do tenant;
 * `test_purchase` ausente = compra real.
 */
function GooglePlayDetails({
  info,
  trialEndsAt,
}: {
  info: GooglePlaySubscriptionInfo | null;
  trialEndsAt: number | null;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const price = info ? formatGooglePlayPrice(info, i18n.language) : null;
  return (
    <Box margin={{ top: 'm' }}>
      <Alert type="info" header={t('tenantDetail.billing.googlePlay.title')}>
        {t('tenantDetail.billing.googlePlay.notice')}
      </Alert>
      {info ? (
        <Box margin={{ top: 's' }}>
          <ColumnLayout columns={2} variant="text-grid">
            <Field
              label={t('tenantDetail.billing.googlePlay.purchaseToken')}
              value={
                <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{info.purchase_token}</span>
              }
            />
            <Field
              label={t('tenantDetail.billing.googlePlay.orderId')}
              value={<span style={{ fontFamily: 'monospace' }}>{info.latest_order_id ?? '—'}</span>}
            />
            <Field label={t('tenantDetail.billing.googlePlay.state')} value={info.state} />
            <Field
              label={t('tenantDetail.billing.googlePlay.environment')}
              value={
                info.test_purchase === true
                  ? t('tenantDetail.billing.googlePlay.envTest')
                  : t('tenantDetail.billing.googlePlay.envProduction')
              }
            />
            <Field label={t('tenantDetail.billing.googlePlay.region')} value={info.region_code ?? '—'} />
            <Field label={t('tenantDetail.billing.googlePlay.price')} value={price ?? '—'} />
            <Field
              label={t('tenantDetail.billing.googlePlay.product')}
              value={
                <span style={{ fontFamily: 'monospace' }}>
                  {info.base_plan_id ? `${info.product_id} · ${info.base_plan_id}` : info.product_id}
                </span>
              }
            />
            <Field label={t('tenantDetail.billing.googlePlay.offerPhase')} value={info.offer_phase ?? '—'} />
            {info.expires_at != null ? (
              <Field
                label={t('tenantDetail.billing.googlePlay.expiresAt')}
                value={formatDate(info.expires_at, i18n.language)}
              />
            ) : null}
            {trialEndsAt !== null ? (
              <Field
                label={t('tenantDetail.billing.googlePlay.trialEndsAt')}
                value={formatDate(trialEndsAt, i18n.language)}
              />
            ) : null}
            <Field
              label={t('tenantDetail.billing.googlePlay.autoRenew')}
              value={
                info.auto_renew == null
                  ? '—'
                  : info.auto_renew
                    ? t('tenantDetail.billing.googlePlay.autoRenewOn')
                    : t('tenantDetail.billing.googlePlay.autoRenewOff')
              }
            />
          </ColumnLayout>
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * Card de Cobrança do detalhe de tenant: plano, valor da parcela (efetivo +
 * tabela riscada quando há desconto), ciclo, status, próximo vencimento (com a
 * nota de prorrogação), cancelamento, cupom, 1º pagamento, último pagamento,
 * nº de cobranças e total pago.
 *
 * Origem: sem issue — commit c5a7bd86a, 2026-04-26 16:52 +0200 (Sprint 2);
 * Cloudscape em 627b442cc, 2026-06-09, para casar com o `TenantInfoCard` (o
 * `SubscriptionCard` Tailwind do ui-shared, hoje removido, destoava).
 *
 * Contrato: recebe o doc já lido (`result: BillingDocResult`) e assina sozinho
 * os pagamentos (`subscribeTenantPayments`, 100 mais recentes) enquanto
 * montado. O resumo conta só pagamentos liquidados com valor líquido > 0
 * (`summarizePayments`). "Aguardando pagamento" usa `needsPayment` — o MESMO
 * gate dos apps.
 *
 * Usado por: `routes/TenantDetailPage.tsx` (rota `/tenants/:id`, abas Visão
 * geral e Cobrança, menu Tenants; spec `docs/screens/tenant-detail-admin.md`)
 * e `routes/BillingOverviewPage.tsx` (rota `/billing/overview/:tenantId`; spec
 * `docs/screens/billing-overview.md`), admin web.
 *
 * Arquitetura: pricing congelado — `amount`/`currency` vêm do doc do tenant,
 * nunca do catálogo. Só leitura.
 *
 * Dados: `tenants/{tenantId}/billing/subscription` (`SubscriptionSchema`,
 * `packages/types/src/schemas.ts`): `amount` em CENTAVOS, `current_period_end`,
 * `renewal_postponed_from/at`, `pending_cycle_change_effective_at` em epoch
 * ms; `discount` opcional. `tenants/{tenantId}/payments/*` (`PaymentSchema`):
 * `amount`/`refunded_amount` em centavos. Rules: leitura por membro do tenant
 * ou `isSuperAdmin()` (`match /payments/{paymentId}` sob o tenant). A query é
 * `orderBy('created_at', 'desc')` + `limit(100)` numa coleção só — índice
 * single-field, sem composto.
 *
 * Falhas conhecidas:
 *  - 2026-06-27: assinatura `ACTIVE` sem pagamento aparecia verde (pedido do
 *    owner); 2026-09-26 (#679): o critério "sem `external_subscription_id`"
 *    marcava cortesia/parceiro/demo como bloqueados — hoje `needsPayment`.
 *  - 2026-06-28: "Valor da parcela" ignorava o desconto; e pagamentos R$0 /
 *    100 % estornados inflavam a contagem (`summarizePayments`).
 *  - 2026-09-22 (#700): ternário anual/mensal mostrava trimestral e semestral
 *    como "Mensal" — hoje `CYCLE_LABEL_KEY` sobre o enum.
 *
 * Histórico:
 *  - 2026-06-10 11:56 +0200 — datas, nº de cobranças, parcela e total pago.
 *  - 2026-06-11 19:41 +0200 — nota de prorrogação do vencimento.
 *  - 2026-09-22 23:47 +0200 — (#700) 4 ciclos.
 *  - 2026-09-26 19:49 +0200 — (#679) `needsPayment` para "Aguardando pagamento".
 *  - 2026-10-10 14:40 +0200 — (#798) rotas, specs, query; janela de 100 nos
 *    helpers (#796).
 *  - 2026-10-12 12:30 +0200 — (#942) campo Provedor e espelho da App Store
 *    (`AppStoreDetails`), só leitura.
 *  - 2026-10-13 17:20 +0200 — (#961) aviso "Cancelamento agendado" com o fim
 *    do acesso; "Próximo vencimento" some (não haverá cobrança).
 *  - 2026-10-14 15:17 +0200 — (#969, M4) provedor Google Play e espelho
 *    `googleplay` (`GooglePlayDetails`), só leitura.
 */
export function BillingCard({ result, tenantId }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [payments, setPayments] = useState<PaymentListResult>({ kind: 'LOADING' });

  useEffect(() => {
    setPayments({ kind: 'LOADING' });
    return subscribeTenantPayments(tenantId, setPayments);
  }, [tenantId]);

  if (result.kind === 'ERROR') {
    return <Alert type="error">{t('tenantDetail.billing.errorLoad')}</Alert>;
  }

  const subscription = result.kind === 'READY' ? result.data : null;
  const cancelScheduled = isCancellationScheduled(subscription);
  const firstPayment = firstPaymentOf(payments);
  const summary = summarizePayments(payments);

  // Subscription `ACTIVE` que ainda espera o 1º pagamento: venda DIRETA sem
  // assinatura no gateway. O paywall do app bloqueia o acesso nesse estado; o
  // painel não pode exibir "ACTIVE" verde como se estivesse paga (pedido do
  // owner 2026-06-27). O predicado é o `needsPayment` de `@etiquetachef/types`
  // — o MESMO gate dos três apps (#679 §4.5): cortesia, parceiro e demo nascem
  // ACTIVE e sem `external_subscription_id`, e o app os libera. Olhar só o id
  // marcava esses tenants como "Aguardando pagamento — bloqueado pelo
  // paywall", contradizendo a lista (`deriveTenantDisplayStatus`).
  const awaitingPayment =
    subscription !== null &&
    subscription.status === SubscriptionStatus.ACTIVE &&
    needsPayment(subscription);

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            subscription ? (
              awaitingPayment ? (
                <StatusIndicator type="pending">
                  {t('tenantDetail.billing.awaitingPayment')}
                </StatusIndicator>
              ) : (
                <StatusIndicator type={STATUS_TYPE[subscription.status]}>
                  {subscription.status}
                </StatusIndicator>
              )
            ) : undefined
          }
        >
          {t('tenantDetail.billing.title')}
        </Header>
      }
    >
      {result.kind === 'LOADING' ? (
        <Spinner />
      ) : subscription === null ? (
        <Box color="text-status-inactive">{t('tenantDetail.billing.notFound')}</Box>
      ) : (
        <>
        {awaitingPayment ? (
          <Box margin={{ bottom: 'm' }}>
            <Alert type="warning" header={t('tenantDetail.billing.awaitingPayment')}>
              {t('tenantDetail.billing.awaitingPaymentNotice')}
            </Alert>
          </Box>
        ) : null}
        {subscription.pending_cycle_change &&
        subscription.pending_cycle_change !== subscription.billing_cycle ? (
          <Box margin={{ bottom: 'm' }}>
            <Alert type="info" header={t('tenantDetail.billing.pendingCycle.label')}>
              {t('tenantDetail.billing.pendingCycle.notice', {
                // Os 4 ciclos (D13): um ternário anual/mensal mostrava
                // trimestral e semestral como "Mensal".
                target: t(CYCLE_LABEL_KEY[subscription.pending_cycle_change]),
                date:
                  subscription.pending_cycle_change_effective_at != null
                    ? formatDate(subscription.pending_cycle_change_effective_at, i18n.language)
                    : formatDate(subscription.current_period_end, i18n.language),
              })}
            </Alert>
          </Box>
        ) : null}
        {/* #961 — fim marcado no gateway: o cliente fica até o fim do período
            pago e o webhook corta. Sem o aviso, ACTIVE parecia assinatura viva. */}
        {cancelScheduled && subscription.cancelled_at !== null ? (
          <Box margin={{ bottom: 'm' }}>
            <Alert type="warning" header={t('tenantDetail.billing.cancelScheduled.label')}>
              {t('tenantDetail.billing.cancelScheduled.notice', {
                cancelledAt: formatDate(subscription.cancelled_at, i18n.language),
                date: formatDate(subscription.current_period_end, i18n.language),
              })}
            </Alert>
          </Box>
        ) : null}
        <ColumnLayout columns={2} variant="text-grid">
          <Field label={t('tenantDetail.billing.plan')} value={subscription.plan_id} />
          {/* #942/#969 — quem cobra: stripe/asaas/appstore/googleplay/manual; ausente em doc antigo. */}
          <Field
            label={t('tenantDetail.billing.provider')}
            value={
              subscription.external_provider === PaymentProvider.APPSTORE
                ? t('tenantDetail.billing.appStore.title')
                : subscription.external_provider === PaymentProvider.GOOGLEPLAY
                  ? t('tenantDetail.billing.googlePlay.title')
                  : (subscription.external_provider ?? '—')
            }
          />
          <Field
            label={t('tenantDetail.billing.amount')}
            value={((): React.ReactNode => {
              const loc = localeForCountry(subscription.country_code);
              // Valor efetivo da parcela com o desconto vigente aplicado
              // (isenção vitalícia → 0; cupom percent/fixo → abatido). Quando
              // há desconto, mostra o efetivo + o preço de tabela riscado.
              const effective = discountedAmountCents(
                subscription.amount,
                subscription.discount ?? null,
                serverNow(),
              );
              const full = (
                <MoneyDisplay
                  amount={subscription.amount}
                  currency={subscription.currency}
                  locale={loc}
                />
              );
              if (effective === subscription.amount) return full;
              return (
                <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'baseline' }}>
                  <MoneyDisplay amount={effective} currency={subscription.currency} locale={loc} />
                  <Box variant="span" color="text-status-inactive">
                    <span style={{ textDecoration: 'line-through' }}>{full}</span>
                  </Box>
                </span>
              );
            })()}
          />
          <Field
            label={t('tenantDetail.billing.cycle')}
            value={t(CYCLE_LABEL_KEY[subscription.billing_cycle])}
          />
          {subscription.status !== SubscriptionStatus.CANCELLED &&
          !cancelScheduled &&
          subscription.current_period_end > 0 ? (
            <Field
              label={t('tenantDetail.billing.nextDue')}
              value={
                <>
                  <span>{formatDate(subscription.current_period_end, i18n.language)}</span>
                  {subscription.renewal_postponed_from != null ? (
                    <Box fontSize="body-s" color="text-status-info">
                      {t('billing.postpone.cardNote', {
                        date: formatDate(
                          subscription.renewal_postponed_at ?? subscription.current_period_end,
                          i18n.language,
                        ),
                        days: Math.round(
                          (subscription.current_period_end -
                            subscription.renewal_postponed_from) /
                            86_400_000,
                        ),
                      })}
                    </Box>
                  ) : null}
                </>
              }
            />
          ) : null}
          {subscription.status === SubscriptionStatus.CANCELLED &&
          subscription.cancelled_at !== null ? (
            <Field
              label={t('tenantDetail.billing.cancelledAt')}
              value={formatDate(subscription.cancelled_at, i18n.language)}
            />
          ) : null}
          {subscription.discount ? (
            <Field
              label={t('tenantDetail.billing.coupon')}
              value={
                <span style={{ fontFamily: 'monospace' }}>
                  {subscription.discount.external_coupon_id}
                </span>
              }
            />
          ) : null}
          {firstPayment ? (
            <Field
              label={t('tenantDetail.billing.firstPayment')}
              value={
                <MoneyDisplay
                  amount={firstPayment.amount}
                  currency={firstPayment.currency}
                  locale={localeForCountry(subscription.country_code)}
                />
              }
            />
          ) : null}
          {summary && summary.lastPaidAt !== null ? (
            <Field
              label={t('tenantDetail.billing.lastPayment')}
              value={formatDate(summary.lastPaidAt, i18n.language)}
            />
          ) : null}
          {summary ? (
            <Field
              label={t('tenantDetail.billing.paymentCount')}
              value={String(summary.count)}
            />
          ) : null}
          {summary ? (
            <Field
              label={t('tenantDetail.billing.totalPaid')}
              value={
                <MoneyDisplay
                  amount={summary.totalPaidCents}
                  currency={subscription.currency}
                  locale={localeForCountry(subscription.country_code)}
                />
              }
            />
          ) : null}
        </ColumnLayout>
        {subscription.external_provider === PaymentProvider.APPSTORE ? (
          <AppStoreDetails
            info={subscription.appstore ?? null}
            trialEndsAt={subscription.trial_ends_at ?? null}
          />
        ) : null}
        {/* Auditoria 2026-10-14 15:17 +0200 (#969, M4): só o espelho `appstore` era mostrado; o tenant pago
            pela Play aparecia sem token, pedido nem preço — o suporte não tinha por onde começar. */}
        {subscription.external_provider === PaymentProvider.GOOGLEPLAY ? (
          <GooglePlayDetails
            info={subscription.googleplay ?? null}
            trialEndsAt={subscription.trial_ends_at ?? null}
          />
        ) : null}
        </>
      )}
    </Container>
  );
}
